import { createCodedError } from '~/common/errors/CodedError';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Normalizes an esign base-query error into the same `CodedError` shape the
 * legacy hand-written esignature methods threw, so consumer error branches
 * (`error instanceof Error`, `error.message`, `getAxiosErrorMessage`) keep
 * behaving identically after the migration to the generated esign API.
 */
export const mapEsignError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  if (isRecord(error)) {
    const { status } = error as { status?: number | string };
    const data = (error as { data?: unknown }).data;

    if (isRecord(data)) {
      if (status === 422 && Array.isArray(data.detail)) {
        const validationError = data.detail[0] as { msg?: string } | undefined;
        return createCodedError({
          message: validationError?.msg || 'Validation error',
          statusCode: 422,
          title: 'Validation Error',
        });
      }

      if (Array.isArray(data.errors) && data.errors.length > 0) {
        const firstError = data.errors[0] as {
          detail?: string;
          title?: string;
          status?: number;
        };
        let statusCode: number = 0;
        if (typeof firstError.status === 'number') {
          statusCode = firstError.status;
        } else if (typeof status === 'number') {
          statusCode = status;
        }
        return createCodedError({
          message:
            firstError.detail ??
            firstError.title ??
            'An unexpected error occurred',
          statusCode: statusCode,
          title: firstError.title,
        });
      }
    }

    if (typeof data === 'string') {
      return new Error(data);
    }

    const fetchError = (error as { error?: unknown }).error;
    if (typeof fetchError === 'string') {
      return new Error(fetchError);
    }
  }

  return new Error('An unexpected error occurred');
};
